import { useState, useEffect } from "react";
import { StatusBadge } from "./status-badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";
import type { StatusConfig } from "@shared/schema";
import { useScrollPreservation } from "@/hooks/use-scroll-preservation";

interface EditableStatusProps {
  status: string;
  type?: 'project' | 'task' | 'pulse';
  onStatusChange: (newStatus: string) => void;
  className?: string;
}

export function EditableStatus({ 
  status, 
  type = 'task', 
  onStatusChange, 
  className 
}: EditableStatusProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [currentStatus, setCurrentStatus] = useState(status);
  const { captureScroll, restoreScroll } = useScrollPreservation();
  
  // Update currentStatus when status prop changes
  useEffect(() => {
    setCurrentStatus(status);
  }, [status]);

  // Fetch status configurations from database
  const { data: statusConfigs = [] } = useQuery<StatusConfig[]>({
    queryKey: ["/api/status-configs"],
  });

  // Filter statuses by category
  const statusConfigsForType = statusConfigs
    .filter(config => config.category === type)
    .sort((a, b) => a.sortOrder - b.sortOrder);

  const handleStatusChange = (newStatus: string) => {
    // Capture scroll position before any changes
    captureScroll();
    
    // Find the config for this display name
    const config = statusConfigsForType.find(c => c.displayName === newStatus || (newStatus === 'empty' && c.displayName === ''));
    // Use the statusKey from the config if found, otherwise use the newStatus as-is
    const actualStatus = config ? config.statusKey : newStatus;
    
    setCurrentStatus(actualStatus);
    onStatusChange(actualStatus);
    setIsEditing(false);
    
    // Restore scroll position after status change
    restoreScroll();
  };

  // Find the display value for the current status
  const getCurrentDisplayValue = () => {
    if (currentStatus === 'empty') {
      return 'empty'; // This will match the value in SelectItem
    }
    const config = statusConfigsForType.find(c => 
      c.displayName === currentStatus || c.statusKey === currentStatus
    );
    return config ? (config.displayName || 'empty') : currentStatus;
  };

  if (isEditing) {
    return (
      <Select
        value={getCurrentDisplayValue()}
        onValueChange={handleStatusChange}
        onOpenChange={(open) => !open && setIsEditing(false)}
        open={true}
      >
        <SelectTrigger className={cn("w-auto min-w-max border-none bg-transparent p-0 h-auto focus:ring-0", className)}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="navy-card border-gray-600">
          {statusConfigsForType.map((config) => (
            <SelectItem 
              key={config.statusKey} 
              value={config.displayName || 'empty'} 
              className="text-gray-300 hover:bg-gray-700"
            >
              <StatusBadge status={config.displayName || 'empty'} type={type} />
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  // Pass the statusKey to StatusBadge for proper color matching
  return (
    <button
      onClick={(e) => {
        e.preventDefault(); // Prevent any default scroll behavior
        // Check if Shift key is held
        if (e.shiftKey) {
          // Quick set to Done/Final based on type
          const quickStatus = type === 'project' ? 'final' : 'done';
          handleStatusChange(quickStatus);
        } else {
          setIsEditing(true);
        }
      }}
      className={cn("hover:opacity-80 transition-opacity cursor-pointer", className)}
      title={`Click to change status${type === 'project' ? ' (Shift+Click for Final)' : ' (Shift+Click for Done)'}`}
    >
      <StatusBadge status={currentStatus} type={type} />
    </button>
  );
}